import type openAi from 'openai';
import type { RunnableToolFunctionWithParse } from 'openai/lib/RunnableFunction';
import { z } from 'zod';

import type { CogniteAtlasAPIClient } from '#components/chat/backends/AtlasAiBackend/client';
import { removeProperties } from '#lib/removeProperties';
import type { ChatCompletionMessage, ChatProviderMessage } from '#components/chat/types';

export const ToolCallbackArgs = z.object({
  id: z.string(),
  args: z.custom(),
  message: z.custom<ChatProviderMessage>(),
  messages: z.custom<ChatCompletionMessage>().array(),
  aiClient: z.custom<CogniteAtlasAPIClient>(),
  setContext: z.custom<(value: object) => void>(),
});
export type ToolCallbackArgs<ArgsType = any> = z.infer<typeof ToolCallbackArgs> & {
  args: ArgsType;
};

export const ToolResult = z.object({
  message: z.json(),
});
export type ToolResult<MessageType = any> = z.infer<typeof ToolResult> & { message: MessageType };

export const ToolComponentProps = z.object({
  id: z.string(),
  output: z.custom(),
  sourceToolCall: z.custom<openAi.ChatCompletionMessageToolCall>().optional(),
  timestamp: z.date(),
});
export type ToolComponentProps<OutputType = any> = z.infer<typeof ToolComponentProps> & { output: OutputType };

export const ToolDefinition = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z.custom<z.ZodType>(),
  handler: z.custom<(args: ToolCallbackArgs) => Promise<ToolResult>>(),
  component: z.custom<React.ComponentType<ToolComponentProps>>().optional(),
});
export type ToolDefinition<
  HandlerArgsType extends ToolCallbackArgs = ToolCallbackArgs,
  ToolResultType extends ToolResult = ToolResult,
> = z.infer<typeof ToolDefinition> & {
  handler: (args: HandlerArgsType) => Promise<ToolResultType>;
};

// FIXME: Use OpenAI (zod) helpers for tool function definitions
export function useTool<const InputType extends z.ZodType, const OutputType, const NameType extends string>(
  name: NameType,
  description: string,
  parameters: InputType,
  handler: (args: ToolCallbackArgs<z.infer<InputType>>) => Promise<ToolResult<OutputType>>,
  component: React.ComponentType<ToolComponentProps<OutputType>>,
) {
  return {
    name,
    description,
    parameters,
    handler,
    component,
  } as const;
}

// FIXME: Use OpenAI (zod) helpers for tool function definitions
export function toolDefinitionToOpenAi(
  tool: ToolDefinition,
): RunnableToolFunctionWithParse<object> & { function: { name: string } } {
  /* Convert tool definition to OpenAI/CDF compatible format */
  return {
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: removeProperties(z.toJSONSchema(tool.parameters, { unrepresentable: 'any' }), [
        '$schema',
        'additionalProperties',
        'strict',
      ]) as RunnableToolFunctionWithParse<object>['function']['parameters'],
      parse: (args) => (args ? JSON.parse(args) : {}),
      function: async (args) => {
        try {
          // TODO: function factory?
          console.warn('Fallback tool handler', { tool, args });
          const { message } = await tool.handler(args);
          return message;
        } catch (e) {
          throw new Error(`[Fallback] Tool invocation failed: ${e}`);
        }
      },
    },
  } as const;
}
